import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import * as fileSaver from 'file-saver';

import { CompetenceService } from './competence.service';
import { CompetenceDialogComponent } from './competence-dialog/competence-dialog.component';
import { MatConfirmDialogComponent } from 'src/app/shared/mat-confirm-dialog/mat-confirm-dialog.component';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { ICompetence } from 'src/app/shared/models/competence';

@Component({
  selector: 'app-competence-list',
  templateUrl: './competence-list.component.html',
  styleUrls: ['./competence-list.component.less']
})
export class CompetenceListComponent implements OnInit, AfterViewInit {

  dataSource = new MatTableDataSource<ICompetence>();
  displayedColumns: string[] = ['libelle', 'categorieNom', 'unitesOrganisationnelles', 'active', 'actions'];
  loading = false;

  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private competenceService: CompetenceService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    private _liveAnnouncer: LiveAnnouncer,
    private router: Router
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loadCompetences();
  }

  goBack(): void {
    this.router.navigate(['/core/admin/centre-competence']);
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (item, property) => (item as any)[property];
  }

  announceSortChange(sortState: Sort): void {
    if (sortState.direction) {
      this._liveAnnouncer.announce(
        `Tri ${sortState.direction === 'asc' ? 'ascendant' : 'descendant'}`
      );
    } else {
      this._liveAnnouncer.announce('Tri réinitialisé');
    }
  }

  // ── Chargement ────────────────────────────────────────────────────────────

  loadCompetences(): void {
    this.loading = true;
    this.competenceService.getAll().subscribe({
      next: (data) => {
        this.dataSource.data = data;
        this.loading = false;
      },
      error: (err) => {
        this.snackbarService.openSnackBar(
          this.extractErrorMessage(err, 'Erreur lors du chargement des compétences'),
          'error-snack-bar'
        );
        this.loading = false;
      }
    });
  }

  // ── Filtre ────────────────────────────────────────────────────────────────

  applyFilter(event: Event): void {
    this.dataSource.filter = (event.target as HTMLInputElement).value.trim().toLowerCase();
  }

  // ── Dialog ────────────────────────────────────────────────────────────────

  openDialog(competence: ICompetence | null = null): void {
    const dialogRef = this.dialog.open(CompetenceDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: competence
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) this.loadCompetences();
    });
  }

  // ── Suppression ───────────────────────────────────────────────────────────

  deleteCompetence(competence: ICompetence): void {
    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      width: '400px',
      data: { message: `Supprimer la compétence "${competence.libelle}" ?` }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.competenceService.delete(competence.id).subscribe({
        next: () => {
          this.snackbarService.openSnackBar('Compétence supprimée avec succès');
          this.dataSource.data = this.dataSource.data.filter(c => c.id !== competence.id);
        },
        error: (err) => {
          this.snackbarService.openSnackBar(
            this.extractErrorMessage(err, 'Erreur lors de la suppression'),
            'error-snack-bar'
          );
        }
      });
    });
  }

  // ── Export ────────────────────────────────────────────────────────────────

  exportData(): void {
    this.competenceService.exportData().subscribe({
      next: (data) => {
        if (!data) {
          this.snackbarService.openSnackBar('Aucune compétence à exporter', 'error-snack-bar');
          return;
        }
        const blob = new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        fileSaver.saveAs(
          blob,
          'Competences_' + new Date().toLocaleDateString().replace(/\//g, '-') + '.xlsx'
        );
        this.snackbarService.openSnackBar('Les compétences ont été exportées avec succès');
      },
      error: (err) => {
        this.snackbarService.openSnackBar(
          this.extractErrorMessage(err, 'Erreur lors de l\'export des compétences'),
          'error-snack-bar'
        );
      }
    });
  }

  // ── Helper erreurs ────────────────────────────────────────────────────────

  private extractErrorMessage(err: any, fallback: string): string {
    const body = err?.error;
    if (body?.message && typeof body.message === 'string') return body.message;
    if (typeof body === 'string') return body;
    return fallback;
  }
}